import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  afterNextRender,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import {
  type TestAccountInfo,
  TestAccountsService,
  curatorLine,
  describeSessionGroup,
} from './test-accounts.service';

const CREDENTIAL_LABEL: Record<string, string> = {
  'webauthn-passwordless': 'passkey',
  webauthn: 'security key (2nd factor)',
  password: 'password (seed)',
  otp: 'one-time code app',
};

/**
 * Info card for one test account (notes/debug-wrapper-v2.md section 2): what it is for, what to
 * avoid it for, and its live realm roles, org, grants, acting identities and sessions, so a
 * developer can decide whether it is the right account to reproduce an issue with.
 *
 * A native modal `<dialog>`: the browser makes the rest of the page inert and handles Escape; this
 * component returns focus to whatever opened it. Read-only; it never signs anyone in.
 */
@Component({
  selector: 'tb-test-account-info-dialog',
  imports: [DatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <dialog #dialog class="dlg" aria-labelledby="tai-title" data-testid="test-account-info"
      (cancel)="onCancel($event)" (close)="closed.emit()">
      <header class="head">
        <h2 id="tai-title">
          @if (info(); as i) { {{ i.displayName }} } @else { {{ username() }} }
        </h2>
        <button type="button" class="close" data-testid="test-account-info-close"
          aria-label="Close account info" (click)="close()">
          <span class="material-symbols-outlined" aria-hidden="true">close</span>
        </button>
      </header>

      @if (state() === 'loading') {
        <p class="muted" data-testid="test-account-info-loading">Loading live account facts…</p>
      } @else if (state() === 'error') {
        <p class="error" role="alert" data-testid="test-account-info-error">
          Could not load this account's info. The debug endpoint may be unavailable on this host.
        </p>
      } @else if (info(); as i) {
        <div class="body">
          <p class="tags">
            <span class="tag">{{ i.audience }}</span>
            <span class="tag" [class.tag--warn]="i.lifecycle !== 'active'" data-testid="test-account-info-lifecycle">{{ lifecycleLabel(i) }}</span>
            @if (i.owner) { <span class="tag">owner: {{ i.owner }}</span> }
            <span class="tag mono" title="Keycloak user id (OIDC sub)">{{ i.subject }}</span>
          </p>

          <p data-testid="test-account-info-curator">{{ curatorLine(i, {contactFallback: 'unassigned'}) }}</p>
          @if (i.blockedReason) { <p class="muted">{{ i.blockedReason.code }} · {{ i.blockedReason.text }}</p> }
          <section aria-labelledby="tai-partner-links" data-testid="test-account-info-partner-links">
            <h3 id="tai-partner-links">Partner links</h3>
            @for (app of i.linkedPartnerApps ?? []; track app) { <span class="tag">{{ app }} · linked</span> }
            <p class="muted">Live remote identity, permissions and launch: {{ i.partnerLinksStatus ?? 'unavailable' }}. Link metadata does not provision or authorize a partner login.</p>
          </section>

          <section aria-labelledby="tai-purpose">
            <h3 id="tai-purpose">Purpose</h3>
            <p>{{ i.purpose }}</p>
          </section>

          <section aria-labelledby="tai-use" data-testid="test-account-info-scenarios">
            <h3 id="tai-use">Use it for</h3>
            <ul>@for (s of i.scenarios; track $index) { <li>{{ s }}</li> }</ul>
          </section>

          @if (i.avoidFor.length) {
            <section aria-labelledby="tai-avoid">
              <h3 id="tai-avoid">Avoid it for</h3>
              <ul>@for (s of i.avoidFor; track $index) { <li>{{ s }}</li> }</ul>
            </section>
          }

          @if (i.knownIssues.length) {
            <section aria-labelledby="tai-issues">
              <h3 id="tai-issues">Known issues</h3>
              <ul>@for (s of i.knownIssues; track $index) { <li>{{ s }}</li> }</ul>
            </section>
          }

          @if (i.coverageGap) {
            <section aria-labelledby="tai-gap">
              <h3 id="tai-gap">Coverage gap it fills</h3>
              <p>{{ i.coverageGap }}</p>
              @if (i.proposedRealm) {
                <p class="muted">Realm roles it will get: {{ i.proposedRealm.realmRoles.join(', ') }}</p>
              }
              @if (i.followUps.length) {
                <ul class="muted">@for (s of i.followUps; track $index) { <li>{{ s }}</li> }</ul>
              }
            </section>
          }

          <section aria-labelledby="tai-realm" data-testid="test-account-info-realm">
            <h3 id="tai-realm">Identity provider (live)</h3>
            @if (i.live.realm.status !== 'ok') {
              <p class="muted">Unavailable: {{ i.live.realm.detail }}</p>
            } @else if (i.live.realm.exists === false) {
              <p class="muted">{{ i.live.realm.detail }}</p>
            } @else {
              <dl>
                <dt>Enabled</dt><dd>{{ i.live.realm.enabled ? 'yes' : 'no' }}</dd>
                <dt>Realm roles</dt><dd>{{ i.live.realm.realmRoles.join(', ') || 'none' }}</dd>
                <dt>Credentials</dt><dd>{{ credentials(i.live.realm.credentialTypes) }}</dd>
              </dl>
            }
          </section>

          <section aria-labelledby="tai-orgs" data-testid="test-account-info-orgs">
            <h3 id="tai-orgs">Organisations (live)</h3>
            @if (i.live.orgs.status !== 'ok') {
              <p class="muted">Unavailable: {{ i.live.orgs.detail }}</p>
            } @else if (i.live.orgs.memberships.length === 0) {
              <p class="muted">No org membership.</p>
            } @else {
              <ul>
                @for (m of i.live.orgs.memberships; track m.orgId) {
                  <li><span class="mono">{{ m.orgId }}</span> as {{ m.role ?? 'member' }}</li>
                }
              </ul>
              @if (i.live.orgs.detail) { <p class="muted">{{ i.live.orgs.detail }}.</p> }
            }
          </section>

          <section aria-labelledby="tai-grants" data-testid="test-account-info-grants">
            <h3 id="tai-grants">Effective grants (live)</h3>
            @if (i.live.grants.status !== 'ok') {
              <p class="muted">{{ i.live.grants.status === 'not-applicable' ? '' : 'Unavailable: ' }}{{ i.live.grants.detail }}</p>
            } @else {
              @for (org of i.live.grants.byOrg; track org.orgId) {
                @if (org.status !== 'ok') {
                  <p class="muted">Grants in <span class="mono">{{ org.orgId }}</span> unavailable.</p>
                } @else if (org.actions.length === 0) {
                  <p class="muted">No live grants in <span class="mono">{{ org.orgId }}</span>.</p>
                } @else {
                  <table class="grants">
                    <caption class="sr-only">Live grants in org {{ org.orgId }}</caption>
                    <thead><tr><th scope="col">Action</th><th scope="col">Scope</th><th scope="col">Velocity left today</th></tr></thead>
                    <tbody>
                      @for (g of org.actions; track g.action) {
                        <tr>
                          <td class="mono">{{ g.action }}</td>
                          <td>{{ scope(g.orgWide, g.accountCount) }}</td>
                          <td [class.exhausted]="g.velocityRemaining === 0">{{ g.velocityRemaining ?? '—' }}</td>
                        </tr>
                      }
                    </tbody>
                  </table>
                  @if (org.truncated) { <p class="muted">More grants exist than one read shows.</p> }
                }
              }
            }
          </section>

          <section aria-labelledby="tai-acting" data-testid="test-account-info-acting">
            <h3 id="tai-acting">Acting identities inside the portal</h3>
            <p class="muted">{{ i.live.personas.note }}</p>
            @if (i.live.personas.keys.length) {
              <p class="chips">@for (k of i.live.personas.keys; track k) { <span class="tag">{{ k }}</span> }</p>
            }
          </section>

          <section aria-labelledby="tai-sessions" data-testid="test-account-info-sessions">
            <h3 id="tai-sessions">Live sessions</h3>
            @if (i.sessions.status !== 'ok') {
              <p class="muted">Session count unavailable.</p>
            } @else if (i.sessions.total === 0) {
              <p class="muted">Nobody is signed in to this account right now.</p>
            } @else {
              <ul>
                @for (g of i.sessions.groups; track $index) {
                  <li><strong>{{ g.count }}</strong> {{ describe(g) }}
                    <span class="muted">, last active {{ g.lastActiveAt | date: 'HH:mm' }}</span></li>
                }
              </ul>
            }
            @if (i.sessions.notObserved.length) {
              <p class="muted">Not visible to Edge's session store: {{ i.sessions.notObserved.join(', ') }}.</p>
            }
          </section>
        </div>
      }
    </dialog>
  `,
  styles: `
    .dlg {
      width: min(36rem, calc(100vw - 2rem)); max-height: min(44rem, calc(100vh - 2rem));
      padding: 0; border: 1px solid var(--tb-border-line, #ccc); border-radius: var(--tb-radius-md, 10px);
      background: var(--tb-surface, #fff); color: var(--tb-text-primary, #222);
      box-shadow: 0 12px 32px rgb(0 0 0 / 0.25);
    }
    .dlg::backdrop { background: rgb(0 0 0 / 0.45); }
    .head {
      position: sticky; top: 0; display: flex; align-items: center; justify-content: space-between; gap: 0.5rem;
      padding: 0.75rem 1rem; border-bottom: 1px solid var(--tb-border-line, #ddd); background: inherit;
    }
    h2 { margin: 0; font-size: 1rem; }
    h3 { margin: 0.9rem 0 0.3rem; font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.04em; color: var(--tb-text-muted, #666); }
    .body { padding: 0.25rem 1rem 1rem; font-size: 0.85rem; line-height: 1.45; }
    .body p, .body ul { margin: 0.2rem 0; }
    .body ul { padding-left: 1.1rem; }
    .close {
      display: inline-grid; place-items: center; width: 32px; height: 32px; flex: none;
      border: 1px solid transparent; border-radius: var(--tb-radius-sm, 6px); background: transparent; color: inherit; cursor: pointer;
    }
    .close:hover { border-color: var(--tb-border-line, #ccc); }
    .close:focus-visible { outline: 2px solid var(--tb-brand-accent, #4f86f7); outline-offset: 1px; }
    .muted { color: var(--tb-text-muted, #666); }
    .error { color: var(--tb-finance-negative, #b00020); padding: 1rem; margin: 0; }
    .tags, .chips { display: flex; flex-wrap: wrap; gap: 0.3rem; margin-top: 0.6rem !important; }
    .tag {
      display: inline-block; padding: 0.1rem 0.45rem; border-radius: 999px; font-size: 0.7rem;
      border: 1px solid var(--tb-border-line, #ccc); background: var(--tb-surface-inset, #f4f5f7);
    }
    .tag--warn { border-color: var(--tb-status-warning, #b26b00); }
    .mono { font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; font-size: 0.75rem; }
    dl { display: grid; grid-template-columns: max-content 1fr; gap: 0.15rem 0.75rem; margin: 0.2rem 0; }
    dt { color: var(--tb-text-muted, #666); }
    dd { margin: 0; }
    .grants { width: 100%; border-collapse: collapse; font-size: 0.78rem; margin: 0.2rem 0; }
    .grants th, .grants td { text-align: left; padding: 0.2rem 0.4rem; border-bottom: 1px solid var(--tb-border-line, #e3e3e3); }
    .grants th { font-weight: 600; color: var(--tb-text-muted, #666); }
    .exhausted { font-weight: 700; }
    .sr-only { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; border: 0; }
  `,
})
export class TestAccountInfoDialogComponent {
  private readonly accounts = inject(TestAccountsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = viewChild.required<ElementRef<HTMLDialogElement>>('dialog');
  private request: Subscription | null = null;
  private returnFocus: HTMLElement | null = null;

  /** The catalogued test account to show. */
  readonly username = input.required<string>();
  /** Emitted once the dialog has closed (button, Escape, or backdrop form submit). */
  readonly closed = output<void>();

  protected readonly info = signal<TestAccountInfo | null>(null);
  protected readonly state = signal<'loading' | 'ok' | 'error'>('loading');
  protected readonly describe = describeSessionGroup;
  protected readonly curatorLine = curatorLine;

  constructor() {
    this.returnFocus = typeof document !== 'undefined' ? (document.activeElement as HTMLElement | null) : null;
    effect(() => {
      const username = this.username();
      untracked(() => this.load(username));
    });
    afterNextRender(() => {
      const el = this.dialog().nativeElement;
      if (el.open) return;
      // jsdom (unit tests) has no showModal; fall back to the open attribute there.
      if (typeof el.showModal === 'function') el.showModal();
      else el.setAttribute('open', '');
    });
    this.destroyRef.onDestroy(() => {
      this.request?.unsubscribe();
      this.returnFocus?.focus?.();
    });
  }

  close(): void {
    const el = this.dialog().nativeElement;
    if (typeof el.close === 'function' && el.open) {
      el.close(); // fires (close), which emits `closed`
      return;
    }
    el.removeAttribute('open');
    this.closed.emit();
  }

  protected onCancel(event: Event): void {
    // Escape: let the native dialog close; (close) then tells the host.
    event.stopPropagation();
  }

  protected lifecycleLabel(i: TestAccountInfo): string {
    switch (i.lifecycle) {
      case 'active':
        return i.signInAvailable ? 'sign-in available' : 'sign-in unavailable';
      case 'ready':
        return 'in the realm, not yet on the debug allow-list';
      case 'not-ready':
        return 'not ready: account setup is incomplete';
      case 'proposed':
        return 'proposed: Make ready has not completed';
      default:
        return i.lifecycle;
    }
  }

  protected credentials(types: string[]): string {
    if (!types.length) return 'none (cannot sign in interactively)';
    return types.map((t) => CREDENTIAL_LABEL[t] ?? t).join(', ');
  }

  protected scope(orgWide: boolean, accountCount: number): string {
    if (orgWide && accountCount > 0) return `org-wide + ${accountCount} account${accountCount === 1 ? '' : 's'}`;
    if (orgWide) return 'org-wide';
    return `${accountCount} account${accountCount === 1 ? '' : 's'}`;
  }

  private load(username: string): void {
    this.request?.unsubscribe();
    this.state.set('loading');
    this.info.set(null);
    this.request = this.accounts
      .info(username)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (info) => {
          // A proxy or stub that answers 200 with some other body is an error, not a blank card.
          if (!info || typeof info !== 'object' || Array.isArray(info) || !info.live || !info.sessions) {
            this.state.set('error');
            return;
          }
          this.info.set(info);
          this.state.set('ok');
        },
        error: () => this.state.set('error'),
      });
  }
}
